import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Award, FileText, Send, CheckCircle2, Circle } from "lucide-react";

// Carnet de progression d'un enfant Petits Samouraïs (étapes → badges → certificats, sans ceintures).
export function CarnetDrawer({ sid, enr, onClose, reload }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { const r = await api.get(`/samourais/sessions/${sid}/enrollment/${enr.eid}/carnet`); setData(r.data); }
    catch { toast.error("Carnet indisponible"); }
  }, [sid, enr.eid]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (skill) => {
    setBusy(skill.id);
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/progress`, { skill_id: skill.id, done: !skill.done });
      setData((d) => ({ ...d, carnet: r.data.carnet }));
      if (r.data.awarded) toast.success(`🏅 ${r.data.awarded.label} décerné à ${enr.child_name} !`, { duration: 6000 });
      reload?.();
    } catch { toast.error("Erreur"); } finally { setBusy(""); }
  };
  const award = async (stage) => {
    if (!window.confirm(`Décerner « ${stage.certificate_title} » à ${enr.child_name} ?\nToutes les habiletés de l'étape seront cochées.`)) return;
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/carnet/award`, { stage_id: stage.id });
      setData((d) => ({ ...d, carnet: r.data.carnet })); toast.success("Certificat décerné 🏅"); reload?.();
    } catch { toast.error("Erreur"); }
  };
  const handed = async (stage, value) => {
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/certificate/${stage.id}/handed`, { handed: value });
      setData((d) => ({ ...d, carnet: r.data.carnet })); toast.success(value ? "Remise notée ✓" : "Remise annulée"); reload?.();
    } catch { toast.error("Erreur"); }
  };
  const openPdf = async (stage) => {
    try {
      const r = await api.get(`/samourais/sessions/${sid}/enrollment/${enr.eid}/certificate/${stage.id}.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })), "_blank");
    } catch { toast.error("PDF indisponible"); }
  };
  const sendPdf = async (stage) => {
    if (!window.confirm(`Envoyer le ${stage.certificate_title} (PDF) au parent (${data.parent_email || "courriel manquant"}) ?\nEnvoi RÉEL par courriel.`)) return;
    setBusy("send" + stage.id);
    try {
      const r = await api.post(`/samourais/sessions/${sid}/enrollment/${enr.eid}/certificate/${stage.id}/send`);
      toast.success(`Certificat envoyé à ${r.data.email} ✓`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Envoi impossible"); } finally { setBusy(""); }
  };

  const carnet = data?.carnet;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="carnet-drawer">
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-2xl border border-white/10 bg-zinc-900 text-zinc-100">
        <div className="sticky top-0 bg-zinc-900/95 backdrop-blur border-b border-white/10 px-5 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] text-red-400 uppercase">Carnet du Petit Samouraï</div>
            <h3 className="text-lg font-bold" data-testid="carnet-child">{enr.child_name}{enr.child_age ? ` · ${enr.child_age} ans` : ""}</h3>
          </div>
          <div className="flex items-center gap-3">
            {carnet && <div className="text-right"><div className="text-xs text-zinc-400">Progression</div><div className="font-bold" data-testid="carnet-pct">{carnet.pct} % · {carnet.badges_earned.length}/{carnet.stages.length} badges</div></div>}
            <button onClick={onClose} data-testid="carnet-close" className="p-1.5 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
          </div>
        </div>
        {!carnet ? <div className="p-8 text-zinc-400">Chargement…</div> : (
          <div className="p-5 space-y-4">
            <p className="text-xs text-zinc-400">{carnet.intro}</p>
            {carnet.stages.map((st) => (
              <div key={st.id} className={`rounded-xl border p-4 ${st.certified ? "border-emerald-500/40 bg-emerald-500/5" : st.id === carnet.current_stage ? "border-white/25 bg-white/[0.04]" : "border-white/10"}`} data-testid={`carnet-stage-${st.id}`}>
                <div className="flex items-center gap-4">
                  <img src={st.badge} alt={st.name} className={`w-16 h-16 rounded-full shrink-0 ${st.certified ? "" : "grayscale opacity-50"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold" style={{ color: st.color }}>{st.name}</span>
                      <span className="text-xs text-zinc-400">— {st.subtitle}</span>
                      {st.certified && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300" data-testid={`carnet-certified-${st.id}`}>🏅 certifié {st.certificate?.date}{st.certificate?.sent_at ? " · envoyé ✓" : ""}</span>}
                      {st.certified && (
                        <label className={`text-[10px] px-2 py-0.5 rounded-full cursor-pointer flex items-center gap-1 ${st.certificate?.handed_at ? "bg-amber-500/25 text-amber-200" : "bg-white/10 text-zinc-300"}`} data-testid={`carnet-handed-${st.id}`}>
                          <input type="checkbox" className="accent-amber-500" checked={!!st.certificate?.handed_at} onChange={(e) => handed(st, e.target.checked)} />
                          {st.certificate?.handed_at ? `remis en main propre le ${st.certificate.handed_at.slice(0, 10)}` : "remis en main propre au dojo"}
                        </label>
                      )}
                    </div>
                    <div className="mt-1.5 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full transition-all" style={{ width: `${st.pct}%`, background: st.color }} /></div>
                    <div className="text-[11px] text-zinc-400 mt-1">{st.done}/{st.total} habiletés</div>
                  </div>
                  <div className="flex flex-col gap-1 shrink-0">
                    {st.certified ? (<>
                      <button onClick={() => openPdf(st)} data-testid={`carnet-pdf-${st.id}`} className="text-[11px] px-2 py-1 rounded bg-amber-600/80 hover:bg-amber-600 flex items-center gap-1"><FileText className="w-3 h-3" /> PDF</button>
                      <button onClick={() => sendPdf(st)} disabled={busy === "send" + st.id} data-testid={`carnet-send-${st.id}`} className="text-[11px] px-2 py-1 rounded bg-sky-600/80 hover:bg-sky-600 flex items-center gap-1"><Send className="w-3 h-3" /> Parent</button>
                    </>) : (
                      <button onClick={() => award(st)} data-testid={`carnet-award-${st.id}`} className="text-[11px] px-2 py-1 rounded bg-white/10 hover:bg-white/20 flex items-center gap-1"><Award className="w-3 h-3" /> Décerner</button>
                    )}
                  </div>
                </div>
                <ul className="mt-3 grid sm:grid-cols-2 gap-1.5">
                  {st.skills.map((s) => (
                    <li key={s.id}>
                      <button onClick={() => toggle(s)} disabled={busy === s.id} data-testid={`carnet-skill-${s.id}`} title={s.hint}
                        className={`w-full text-left flex items-start gap-2 rounded-lg px-2.5 py-2 text-sm border ${s.done ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 hover:bg-white/5"}`}>
                        {s.done ? <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" /> : <Circle className="w-4 h-4 text-zinc-500 mt-0.5 shrink-0" />}
                        <span><span className={s.done ? "" : "text-zinc-200"}>{s.label}</span>{s.video_url && <span className="ml-1.5 text-emerald-300" title="Vidéo disponible">🎬</span>}<span className="block text-[11px] text-zinc-500">{s.hint}</span></span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
